// src/components/EmployeeAccessStep.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { employeeAccessApi } from '../services/employeeAccessApi';
import InfoModal from '../components/common/InfoModal';
import { apiClient } from '../utils/apiClient';
import { Loader2, ShieldAlert, Key, Fingerprint, Settings, ChevronDown, ChevronUp, CheckCircle2, XCircle, CreditCard, Plus, Trash2, X, QrCode, Smartphone } from 'lucide-react';

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

const CREDENTIAL_TYPES = { Card: 1, Finger: 2, Face: 3, Pin: 4, VirtualQr: 5, Nfc: 6 };

const FINGER_LABELS = {
    1: 'Sağ Baş Parmak',
    2: 'Sağ İşaret Parmağı',
    3: 'Sağ Orta Parmak',
    4: 'Sağ Yüzük Parmağı',
    5: 'Sağ Serçe Parmak',
    6: 'Sol Baş Parmak',
    7: 'Sol İşaret Parmağı',
    8: 'Sol Orta Parmak',
    9: 'Sol Yüzük Parmağı',
    10: 'Sol Serçe Parmak'
};

export default function EmployeeAccessStep({ employee, refreshKey = 0, wasJustDeactivated = false }) {
    const employeeId = employee?.id;

    // Data
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [cardNo, setCardNo] = useState('');
    const [credentials, setCredentials] = useState([]);
    const [permissions, setPermissions] = useState([]);

    //Kart Format
    const [cardFormats, setCardFormats] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const debouncedCardNo = useDebounce(cardNo, 500);

    // UI state
    const [loading, setLoading] = useState(false);
    const [cardBusy, setCardBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Cancel Polling Reference
    const abortTokenRef = useRef(false);

    useEffect(() => {
        return () => {
            abortTokenRef.current = true;
        };
    }, []);

    // Confirm Modal State
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    // 409 çatışma modalları
    const [cardConflict, setCardConflict] = useState(null);
    const [saveConflict, setSaveConflict] = useState(null);

    // === Yetki Grupları: UI state ===
    const [agLoading, setAgLoading] = useState(false);
    const [agError, setAgError] = useState(null);
    const [allGroups, setAllGroups] = useState([]);              // dropdown için {id, name}
    const [employeeGroups, setEmployeeGroups] = useState([]);     // personelin üyelikleri
    const [selectedGroupId, setSelectedGroupId] = useState(null); // dropdown seçimi
    const [selectedMembershipIds, setSelectedMembershipIds] = useState(new Set()); // tabloda seçim

    // === Priority Mode ===
    const [priorityMode, setPriorityMode] = useState(0);

    // === Parmak İzi State ===
    const [fingerIndex, setFingerIndex] = useState(1);
    const [fingerBusy, setFingerBusy] = useState(false);
    const [fingerData, setFingerData] = useState(null); // Base64
    const [enrolledFingers, setEnrolledFingers] = useState([]);

    useEffect(() => {
        let active = true;
        if (employee) {
            setPriorityMode(employee.priorityMode ?? 0);
            const fetchFreshDetails = async () => {
                try {
                    const freshEmp = await apiClient(`Employee/${employee.id}`);
                    if (active && freshEmp) {
                        setPriorityMode(freshEmp.priorityMode ?? 0);
                    }
                } catch (e) {
                    console.warn("Priority sync failed:", e);
                }
            };
            fetchFreshDetails();
        }
        return () => { active = false; };
    }, [employee]);

    const handlePriorityToggle = async () => {
        const newVal = priorityMode === 0 ? 1 : 0;
        setPriorityMode(newVal);
        try {
            setAgLoading(true);
            await employeeAccessApi.updatePriority(employeeId, newVal);
            setMsg(`Öncelik güncellendi: ${newVal === 0 ? 'Yetkili Baskın' : 'Yasaklı Baskın'}`);
        } catch (e) {
            setErr('Öncelik güncellenemedi: ' + e.message);
            setPriorityMode(priorityMode); // revert
        } finally {
            setAgLoading(false);
        }
    };

    const ready = useMemo(() => !!employeeId, [employeeId]);

    const loadLists = async (withDevices = true) => {
        if (!ready) return;
        try {
            setLoading(true);
            setErr(null);
            const promises = [
                employeeAccessApi.getCredentials(employeeId),
                employeeAccessApi.getPermissions(employeeId),
                employeeAccessApi.getFingerprints(employeeId),
            ];
            if (withDevices) promises.unshift(employeeAccessApi.getDevices());

            const results = await Promise.all(promises);

            if (withDevices) {
                const [devs, creds, perms, fps] = results;
                setDevices(devs || []);
                if (!selectedDeviceId && (devs?.length || 0) > 0) setSelectedDeviceId(devs[0].id);
                setCredentials(creds || []);
                setPermissions(perms || []);
                setEnrolledFingers(fps || []);
            } else {
                const [creds, perms, fps] = results;
                setCredentials(creds || []);
                setPermissions(perms || []);
                setEnrolledFingers(fps || []);
            }
        } catch (e) {
            setErr(e?.message || 'Kayıtlar yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const loadAccessGroupsUI = async () => {
        if (!ready) return;
        try {
            setAgLoading(true);
            setAgError(null);
            const [dd, mems] = await Promise.all([
                employeeAccessApi.listAccessGroupsForDropdown(),
                employeeAccessApi.listEmployeeGroups(employeeId)
            ]);
            setAllGroups(dd || []);
            setEmployeeGroups(mems || []);
            setSelectedMembershipIds(new Set());
        } catch (e) {
            setAgError(e?.message || 'Yetki grupları yüklenemedi.');
        } finally {
            setAgLoading(false);
        }
    };

    const addGroupToEmployee = async () => {
        if (!selectedGroupId) return;
        try {
            setAgLoading(true);
            await employeeAccessApi.assignEmployeeGroups(employeeId, [selectedGroupId], {
                isFullDay: true, validFrom: null, validTo: null
            });
            await loadAccessGroupsUI();
            setMsg('Grup üyeliği başarıyla eklendi.');
        } catch (e) {
            setErr(e?.message || 'Grup eklenemedi.');
        } finally {
            setAgLoading(false);
        }
    };

    const revokeMembership = (membershipId) => {
        if (!membershipId) return;
        setConfirmModal({
            isOpen: true,
            title: 'Gruptan Çıkar',
            message: 'Bu grup üyeliğini kaldırmak istiyor musunuz?',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    setAgLoading(true);
                    await employeeAccessApi.revokeEmployeeMembership(employeeId, membershipId);
                    await loadAccessGroupsUI();
                    setMsg('Üyelik kaldırıldı.');
                } catch (e) {
                    setErr(e?.message || 'Üyelik kaldırılamadı.');
                } finally {
                    setAgLoading(false);
                }
            }
        });
    };

    const bulkApplyForSelected = async (operation = 'Grant') => {
        try {
            setAgLoading(true);
            const ids = Array.from(selectedMembershipIds || []);
            if (ids.length === 0) {
                setErr('Lütfen tablodan en az bir üyelik seçin.');
                return;
            }

            if (operation === 'Revoke') {
                await Promise.all(ids.map(mid =>
                    employeeAccessApi.revokeEmployeeMembership(employeeId, mid)
                ));
                setMsg('Seçili üyelikler kaldırıldı.');
            } else {
                await Promise.all(ids.map(mid =>
                    employeeAccessApi.updateMembership(employeeId, mid, {}) 
                ));
                setMsg('Seçili üyelikler cihazlara senkronize ediliyor...');
            }

            await loadAccessGroupsUI();
        } catch (e) {
            setErr(e?.message || 'İşlem başarısız.');
        } finally {
            setAgLoading(false);
        }
    };

    const toggleMembershipSelect = (mid) => {
        setSelectedMembershipIds(prev => {
            const copy = new Set(prev);
            if (copy.has(mid)) copy.delete(mid); else copy.add(mid);
            return copy;
        });
    };

    useEffect(() => {
        const fetchFormats = async () => {
            if (debouncedCardNo && /^\d+$/.test(debouncedCardNo)) {
                setIsConverting(true);
                try {
                    const result = await employeeAccessApi.convertCardNumber(debouncedCardNo);
                    setCardFormats(result);
                } catch (error) {
                    console.error("Card format conversion failed:", error);
                    setCardFormats(null);
                } finally {
                    setIsConverting(false);
                }
            } else {
                setCardFormats(null);
            }
        };

        fetchFormats();
    }, [debouncedCardNo]);

    useEffect(() => {
        loadLists(true);
    }, [employeeId, refreshKey]);

    useEffect(() => {
        loadAccessGroupsUI();
    }, [employeeId, refreshKey]);

    const handleCancelPolling = async () => {
        abortTokenRef.current = true;
    };

    const handleCaptureCard = async () => {
        if (!employeeId) return setErr('Önce personeli kaydedin.');
        if (!selectedDeviceId) return setErr('Lütfen cihaz seçin.');

        abortTokenRef.current = false;
        setCardNo('');
        setCardBusy(true);
        setErr(null);
        setMsg(null);

        const dev = devices.find(d => d.id === Number(selectedDeviceId));

        try {
            if (dev?.ipAddress && dev?.port) {
                await employeeAccessApi.cancelLive(dev.ipAddress, dev.port).catch(() => {});
            }

            const start = await employeeAccessApi.startCardCapture(employeeId, Number(selectedDeviceId), 90);
            const opId = start?.operationId;
            if (!opId) throw new Error('Kart okuma işlemi başlatılamadı.');

            let tries = 0;
            const maxTries = 90;
            while (tries < maxTries) {
                if (abortTokenRef.current) {
                    if (dev?.ipAddress && dev?.port) {
                        await employeeAccessApi.cancelLive(dev.ipAddress, dev.port).catch(() => {});
                    }
                    setErr('Kart okuma işlemi iptal edildi.');
                    setCardBusy(false);
                    return;
                }

                const res = await employeeAccessApi.pollCardCapture(opId);
                if (res?.status === 'success' && res?.cardNo) {
                    setCardNo(res.cardNo);
                    setMsg('Kart başarıyla okundu!');
                    setCardBusy(false);
                    return;
                }
                if (res?.status === 'error') {
                    throw new Error(res?.error || 'Kart okunurken bir hata oluştu.');
                }
                await new Promise(r => setTimeout(r, 1000));
                tries++;
            }
            if (!abortTokenRef.current) throw new Error('Zaman aşımı: Kart okunamadı.');
        } catch (e) {
            setErr(e?.message || 'Kart okuma başarısız oldu.');
        } finally {
            setCardBusy(false);
        }
    };

    const handleSaveOnly = async () => {
        if (!employeeId) return setErr('Önce personeli kaydedin.');
        const clean = (cardNo || '').trim();
        if (!clean) return setErr('Kart numarası girin.');

        try {
            setLoading(true); setErr(null); setMsg(null);
            const newCredential = await employeeAccessApi.addCredential(employeeId, {
                employeeId, type: CREDENTIAL_TYPES.Card, value: clean,
            });

            if (newCredential?.isActive) {
                setMsg('Kart başarıyla veritabanına kaydedildi.');
            } else {
                setMsg('Personel aktif olmadığı için yeni kart PASİF olarak kaydedildi.');
            }
            setCardNo('');
            await loadLists(false);
        } catch (e) {
            if (e.status === 409 && (e.code === 'EMPLOYEE_HAS_ACTIVE_CARD' || e.code === 'CARD_IN_USE_BY_ANOTHER')) {
                setSaveConflict({ type: e.code, cardNo: clean, ownerInfo: e.ownerInfo });
            } else {
                setErr(e?.message || 'Kart kaydı başarısız.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateQr = async () => {
        if (!employeeId) return setErr('Önce personeli kaydedin.');

        try {
            setLoading(true); setErr(null); setMsg(null);
            const newCredential = await employeeAccessApi.generateQrCard(employeeId);

            if (newCredential?.isActive) {
                setMsg('Sanal QR Kart başarıyla üretildi ve veritabanına kaydedildi.');
            } else {
                setMsg('Personel aktif olmadığı için yeni Sanal QR Kart PASİF olarak kaydedildi.');
            }
            await loadLists(false);
        } catch (e) {
            if (e.status === 409 && (e.code === 'EMPLOYEE_HAS_ACTIVE_CARD' || e.code === 'CARD_IN_USE_BY_ANOTHER')) {
                setSaveConflict({ type: e.code, cardNo: e.cardNo || 'QR', ownerInfo: e.ownerInfo });
            } else {
                setErr(e?.message || 'QR Kart üretimi başarısız.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateNfc = async () => {
        if (!employeeId) return setErr('Önce personeli kaydedin.');

        try {
            setLoading(true); setErr(null); setMsg(null);
            const newCredential = await employeeAccessApi.generateNfcCard(employeeId);

            if (newCredential?.isActive) {
                setMsg('Sanal NFC Kart başarıyla üretildi ve veritabanına kaydedildi.');
            } else {
                setMsg('Personel aktif olmadığı için yeni Sanal NFC Kart PASİF olarak kaydedildi.');
            }
            await loadLists(false);
        } catch (e) {
            if (e.status === 409 && (e.code === 'EMPLOYEE_HAS_ACTIVE_CARD' || e.code === 'CARD_IN_USE_BY_ANOTHER')) {
                setSaveConflict({ type: e.code, cardNo: e.cardNo || 'NFC', ownerInfo: e.ownerInfo });
            } else {
                setErr(e?.message || 'NFC Kart üretimi başarısız.');
            }
        } finally {
            setLoading(false);
        }
    };

    const deleteCredential = (credentialId) => {
        setConfirmModal({
            isOpen: true,
            title: 'Kimliği Sil',
            message: 'Bu kimliği personelden silmek istediğinizden emin misiniz?',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    setLoading(true); setErr(null); setMsg(null);
                    await employeeAccessApi.deleteCredential(employeeId, credentialId);
                    setCredentials(prev => prev.filter(c => c.id !== credentialId));
                    setMsg('Kimlik başarıyla silindi.');
                } catch (e) {
                    setErr(e?.message || 'Kimlik silinemedi.');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleFingerCapture = async () => {
        if (!employeeId) return setErr('Önce personeli seçin.');
        if (!selectedDeviceId) return setErr('Cihaz seçin.');

        abortTokenRef.current = false;
        setFingerBusy(true); setErr(null); setMsg(null); setFingerData(null);
        
        const dev = devices.find(d => d.id === Number(selectedDeviceId));
        
        try {
            if (dev?.ipAddress && dev?.port) {
                await employeeAccessApi.cancelLive(dev.ipAddress, dev.port).catch(() => {});
            }

            const start = await employeeAccessApi.startFingerCapture(employeeId, Number(selectedDeviceId), Number(fingerIndex));
            const opId = start?.operationId;
            if (!opId) throw new Error('Başlatılamadı');

            let tries = 0;
            while (tries < 40) {
                if (abortTokenRef.current) {
                    if (dev?.ipAddress && dev?.port) {
                        await employeeAccessApi.cancelLive(dev.ipAddress, dev.port).catch(() => {});
                    }
                    setErr('Parmak izi okuma işlemi iptal edildi.');
                    setFingerBusy(false);
                    return;
                }

                const res = await employeeAccessApi.pollFingerCapture(opId);
                if (res?.status === 'success' && res.fingerData) {
                    setFingerData(res.fingerData);
                    setMsg('Parmak izi okundu! Kaydet butonuna basın.');
                    setFingerBusy(false);
                    return;
                }
                if (res?.status === 'error' || res?.status === 'timeout') {
                    throw new Error(res?.error || 'Zaman aşımı');
                }
                await new Promise(r => setTimeout(r, 1000));
                tries++;
            }
            if (!abortTokenRef.current) throw new Error('Zaman aşımı');
        } catch (e) {
            setErr('Parmak izi hatası: ' + e.message);
        } finally {
            setFingerBusy(false);
        }
    };

    const handleSaveFinger = async () => {
        if (!fingerData) return;
        try {
            setLoading(true);
            await employeeAccessApi.saveFingerprint(employeeId, {
                fingerPrintID: Number(fingerIndex),
                fingerData: fingerData,
                algorithm: 'Hikvision'
            });
            setMsg('Parmak izi başarıyla kaydedildi.');
            setFingerData(null);
            await loadLists(false);
        } catch (e) {
            setErr('Kaydetme başarısız: ' + e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFinger = (idx) => {
        setConfirmModal({
            isOpen: true,
            title: 'Parmak İzi Sil',
            message: `${FINGER_LABELS[idx] || `Parmak #${idx}`} verisini silmek istediğinizden emin misiniz?`,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    setLoading(true);
                    await employeeAccessApi.deleteFingerprint(employeeId, idx);
                    setMsg('Parmak izi başarıyla silindi.');
                    await loadLists(false);
                } catch (e) {
                    setErr(e.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const retrySaveWith = async (options = {}) => {
        if (saveConflict?.cardNo === 'QR') {
            await apiClient(`Employee/${employee.id}/credentials/generate-qr`, {
                method: 'POST',
                queryParams: {
                    replace: options.replace ? 'true' : undefined,
                },
            });
        } else if (saveConflict?.cardNo === 'NFC') {
            await apiClient(`Employee/${employee.id}/credentials/generate-nfc`, {
                method: 'POST',
                queryParams: {
                    replace: options.replace ? 'true' : undefined,
                },
            });
        } else {
            const clean = (cardNo || '').trim();
            await apiClient(`Employee/${employee.id}/credentials`, {
                method: 'POST',
                body: {
                    employeeId: employee.id,
                    type: 1, // Card
                    value: clean,
                    isActive: true,
                },
                queryParams: {
                    takeover: options.takeover ? 'true' : undefined,
                    replace: options.replace ? 'true' : undefined,
                },
            });
            setCardNo('');
        }
        await loadLists(false);
    };

    if (!ready) {
        return (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                <ShieldAlert className="w-8 h-8 mx-auto mb-3 text-gray-400" />
                Önce personel temel bilgilerini kaydedin, ardından Geçiş Kontrol adımına geçebilirsiniz.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {wasJustDeactivated && (
                <div className="flex items-start p-4 text-orange-800 bg-orange-50 rounded-xl border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800/50 dark:text-orange-300">
                    <ShieldAlert className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                        Personelin durumu değiştirildiği için, kendisine atanmış tüm kimlik bilgileri (kart, parmak izi vb.) otomatik olarak pasif hale getirilmiştir.
                    </p>
                </div>
            )}

            {/* Mesaj Alanı */}
            {(msg || err) && (
                <div className={`p-4 rounded-xl border flex items-start ${err ? 'bg-red-50 border-red-100 text-red-700 dark:bg-red-900/20 dark:border-red-900/50 dark:text-red-400' : 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-900/50 dark:text-emerald-400'}`}>
                    {err ? <XCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />}
                    <div className="text-sm font-medium">{err || msg}</div>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                
                {/* SOL KOLON: GEÇİŞ İZİNLERİ */}
                <div className="bg-white dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col">
                    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/50 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                        <div className="flex items-center text-gray-800 dark:text-white">
                            <Key className="w-5 h-5 mr-2 text-indigo-500" />
                            <h3 className="font-semibold text-lg">Geçiş İzinleri</h3>
                        </div>
                        {agLoading && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
                    </div>
                    
                    <div className="p-5 flex-1 flex flex-col space-y-5">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            Personelin hangi kapı, turnike ve bölgelerden geçebileceğini belirleyin. Eklenen gruplar cihazlara otomatik olarak iletilir.
                        </div>

                        <div className="flex gap-3">
                            <select
                                value={selectedGroupId || ''}
                                onChange={(e) => setSelectedGroupId(e.target.value || null)}
                                className="flex-1 px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm transition-all"
                            >
                                <option value="">Yetki Grubu Seçin...</option>
                                {(allGroups || []).map(g => {
                                    const alreadyAssigned = (employeeGroups || []).some(
                                        eg => String(eg.groupId) === String(g.id) || eg.groupName === g.name
                                    );
                                    return (
                                        <option key={g.id} value={g.id} disabled={alreadyAssigned}>
                                            {g.name}{alreadyAssigned ? ' (Zaten Ekli)' : ''}
                                        </option>
                                    );
                                })}
                            </select>
                            <button
                                type="button"
                                onClick={addGroupToEmployee}
                                disabled={!selectedGroupId || agLoading}
                                className="px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-4 h-4 mr-1" /> Ekle
                            </button>
                        </div>

                        <div className="flex-1 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden flex flex-col">
                            <div className="bg-gray-50 dark:bg-gray-800/80 px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
                                Tanımlı Gruplar ({employeeGroups.length})
                            </div>
                            <div className="overflow-y-auto max-h-60 custom-scrollbar p-2">
                                {employeeGroups.length === 0 ? (
                                    <div className="text-center py-6 text-sm text-gray-400 dark:text-gray-500">
                                        Henüz atanmış bir yetki grubu yok.
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {employeeGroups.map(m => (
                                            <div key={m.membershipId} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg group hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors">
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 rounded-full bg-emerald-500 mr-3"></div>
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 block">{m.groupName}</span>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">{m.status}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => revokeMembership(m.membershipId)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                                    title="Gruptan Çıkar"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* SAĞ KOLON: GEÇİŞ YÖNTEMLERİ */}
                <div className="bg-white dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col">
                    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/50 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                        <div className="flex items-center text-gray-800 dark:text-white">
                            <CreditCard className="w-5 h-5 mr-2 text-indigo-500" />
                            <h3 className="font-semibold text-lg">Geçiş Yöntemleri</h3>
                        </div>
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
                    </div>

                    <div className="p-5 flex-1 flex flex-col space-y-6">
                        
                        {/* Device Selection (Shared for Live Read) */}
                        <div className="bg-indigo-50/50 dark:bg-indigo-900/10 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800/30">
                            <label className="block text-xs font-semibold text-indigo-900 dark:text-indigo-300 uppercase tracking-wider mb-2">Canlı Okutma İçin Cihaz</label>
                            <select
                                value={selectedDeviceId}
                                onChange={(e) => setSelectedDeviceId(e.target.value)}
                                className="w-full px-3 py-2.5 bg-white dark:bg-gray-900 border border-indigo-200 dark:border-indigo-700/50 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm transition-all"
                            >
                                <option value="">Cihaz Seçin...</option>
                                {devices.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {(d.deviceName || `${d.brand || ''} ${d.model || ''}`.trim())} ({d.ipAddress})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* KART KAYDI */}
                        <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                                <CreditCard className="w-4 h-4 mr-2 text-gray-400" /> Kart Tanımlama
                            </h4>
                            
                            {cardBusy ? (
                                <div className="p-6 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl flex flex-col items-center justify-center text-center animate-pulse">
                                    <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin mb-3" />
                                    <p className="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-1">Cihazdan Kart Bekleniyor...</p>
                                    <p className="text-xs text-indigo-700/70 dark:text-indigo-400/70 mb-4">Lütfen kartı seçili cihaza okutun.</p>
                                    <button 
                                        onClick={handleCancelPolling}
                                        className="px-4 py-1.5 bg-white dark:bg-gray-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md text-sm font-medium transition-colors"
                                    >
                                        İptal Et
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={cardNo}
                                            onChange={(e) => setCardNo(e.target.value)}
                                            placeholder="Kart No (Manuel yazın veya cihazdan okutun)"
                                            className="w-full flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:text-white text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSaveOnly}
                                            disabled={loading || !cardNo.trim()}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                                        >
                                            Kaydet
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={handleGenerateQr}
                                            disabled={loading}
                                            className="flex-1 min-w-[120px] px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 dark:text-blue-300 rounded-lg text-sm font-medium transition-colors whitespace-nowrap text-center"
                                            title="Uygulama üzerinden geçiş için otomatik sanal kart numarası üretir"
                                        >
                                            Sanal QR Üret
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleGenerateNfc}
                                            disabled={loading}
                                            className="flex-1 min-w-[120px] px-3 py-2 bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:hover:bg-fuchsia-900/60 dark:text-fuchsia-300 rounded-lg text-sm font-medium transition-colors whitespace-nowrap text-center"
                                            title="Uygulama üzerinden geçiş için otomatik sanal NFC numarası üretir"
                                        >
                                            Sanal NFC Üret
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCaptureCard}
                                            disabled={loading}
                                            className="flex-1 min-w-[120px] px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 dark:bg-indigo-900/40 dark:hover:bg-indigo-900/60 dark:text-indigo-300 rounded-lg text-sm font-medium transition-colors whitespace-nowrap text-center"
                                        >
                                            Cihazdan Oku
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* PARMAK İZİ KAYDI */}
                        <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                                <Fingerprint className="w-4 h-4 mr-2 text-gray-400" /> Parmak İzi Tanımlama
                            </h4>
                            
                            {fingerBusy ? (
                                <div className="p-6 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl flex flex-col items-center justify-center text-center animate-pulse">
                                    <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin mb-3" />
                                    <p className="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-1">Cihazdan Parmak İzi Bekleniyor...</p>
                                    <p className="text-xs text-indigo-700/70 dark:text-indigo-400/70 mb-4">{FINGER_LABELS[fingerIndex]} okutuluyor.</p>
                                    <button 
                                        onClick={handleCancelPolling}
                                        className="px-4 py-1.5 bg-white dark:bg-gray-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md text-sm font-medium transition-colors"
                                    >
                                        İptal Et
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-3 items-center">
                                    <select
                                        value={fingerIndex}
                                        onChange={e => setFingerIndex(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:text-white text-sm"
                                    >
                                        {Object.entries(FINGER_LABELS).map(([idx, label]) => (
                                            <option key={idx} value={idx}>{label}</option>
                                        ))}
                                    </select>
                                    
                                    {fingerData ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-emerald-600 flex items-center bg-emerald-50 px-2 py-1 rounded">
                                                <CheckCircle2 className="w-3 h-3 mr-1" /> Alındı
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleSaveFinger}
                                                disabled={loading}
                                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                                            >
                                                Kaydet
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleFingerCapture}
                                            disabled={loading}
                                            className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 dark:bg-indigo-900/40 dark:hover:bg-indigo-900/60 dark:text-indigo-300 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            Cihazdan Oku
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* KAYITLI KİMLİKLER ÖZETİ */}
                        {(credentials.length > 0 || enrolledFingers.length > 0) && (
                            <div className="pt-5 mt-auto border-t border-gray-100 dark:border-gray-800">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Kayıtlı Kimlikler</h4>
                                <div className="flex flex-col gap-2">
                                    {credentials.map(c => (
                                        <div 
                                            key={c.id} 
                                            className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
                                                !c.isActive 
                                                ? 'bg-gray-50 border-gray-200 text-gray-500 opacity-80 dark:bg-gray-800/50 dark:border-gray-700 dark:text-gray-400'
                                                : c.type === 5 
                                                    ? 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800/50 dark:text-blue-300'
                                                : c.type === 6 
                                                    ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800 dark:bg-fuchsia-900/20 dark:border-fuchsia-800/50 dark:text-fuchsia-300'
                                                : 'bg-indigo-50 border-indigo-200 text-indigo-800 dark:bg-indigo-900/20 dark:border-indigo-800/50 dark:text-indigo-300'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`p-1.5 rounded-md flex-shrink-0 ${
                                                    !c.isActive ? 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                                    : c.type === 5 ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                                                    : c.type === 6 ? 'bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/40 dark:text-fuchsia-400'
                                                    : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'
                                                }`}>
                                                    {c.type === 5 || c.type === 'VirtualQr' ? (
                                                        <QrCode className="w-4 h-4" />
                                                    ) : c.type === 6 || c.type === 'Nfc' ? (
                                                        <Smartphone className="w-4 h-4" />
                                                    ) : (
                                                        <CreditCard className="w-4 h-4" />
                                                    )}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className={`text-sm font-medium ${!c.isActive ? 'line-through' : ''}`}>
                                                        {c.value}
                                                    </span>
                                                    <span className={`text-[10px] font-semibold tracking-wider uppercase mt-0.5 ${
                                                        !c.isActive ? 'text-red-500 dark:text-red-400'
                                                        : (c.type === 5 || c.type === 'VirtualQr') ? 'text-blue-600 dark:text-blue-400'
                                                        : (c.type === 6 || c.type === 'Nfc') ? 'text-fuchsia-600 dark:text-fuchsia-400'
                                                        : 'text-indigo-600 dark:text-indigo-400'
                                                    }`}>
                                                        {!c.isActive ? 'Pasif (Devredilmiş / İptal)' : (
                                                            (c.type === 5 || c.type === 'VirtualQr') ? 'Sanal QR Kart' :
                                                            (c.type === 6 || c.type === 'Nfc') ? 'Sanal NFC Kart' :
                                                            'Fiziksel Kart'
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => deleteCredential(c.id)} 
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors" 
                                                title="Bu kartı personelden tamamen sil"
                                            >
                                                <X className="w-4 h-4"/>
                                            </button>
                                        </div>
                                    ))}
                                    {enrolledFingers.map(f => (
                                        <div key={f.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800/50 dark:text-blue-300">
                                            <div className="flex items-center gap-3">
                                                <div className="p-1.5 rounded-md flex-shrink-0 bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
                                                    <Fingerprint className="w-4 h-4" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">
                                                        {FINGER_LABELS[f.fingerPrintID]}
                                                    </span>
                                                    <span className="text-[10px] font-semibold tracking-wider uppercase mt-0.5 text-blue-600 dark:text-blue-400">
                                                        Aktif Parmak İzi
                                                    </span>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleDeleteFinger(f.fingerPrintID)} 
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors" 
                                                title="Parmak izini sil"
                                            >
                                                <X className="w-4 h-4"/>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                    </div>
                </div>

            </div>

            {/* GELİŞMİŞ DONANIM AYARLARI */}
            <div className="bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300">
                <button 
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="w-full px-5 py-4 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                >
                    <div className="flex items-center text-gray-700 dark:text-gray-300">
                        <Settings className="w-4 h-4 mr-2" />
                        <span className="font-semibold text-sm">Gelişmiş Donanım Ayarları</span>
                    </div>
                    {showAdvanced ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                
                {showAdvanced && (
                    <div className="p-5 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-8">
                        
                        {/* Yetki Çakışma Önceliği */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Yetki Çakışma Önceliği</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Bir personel aynı cihaza hem Yetkili hem Yasaklı gruplardan erişiyorsa hangisinin geçerli olacağını belirleyin.</p>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={priorityMode === 1}
                                    onChange={handlePriorityToggle}
                                    disabled={agLoading}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
                                <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                                    {priorityMode === 1 ? 'Yasaklı Baskın (Güvenlik Öncelikli)' : 'Yetkili Baskın (Geçiş Öncelikli)'}
                                </span>
                            </label>
                        </div>

                        {/* Manuel Senkronizasyon (Eski Tablo Sağ Aksiyonları) */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Manuel Senkronizasyon Tetikleme</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Seçili grupları zorla cihazlara göndermek veya kaldırmak için bu alanı kullanın.</p>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => bulkApplyForSelected('Grant')}
                                    disabled={agLoading || selectedMembershipIds.size === 0}
                                    className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
                                >
                                    Push (Yolla)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => bulkApplyForSelected('Revoke')}
                                    disabled={agLoading || selectedMembershipIds.size === 0}
                                    className="px-3 py-2 rounded border border-red-200 dark:border-red-800/50 hover:bg-red-50 dark:hover:bg-red-900/30 text-sm font-medium text-red-600 dark:text-red-400 disabled:opacity-50"
                                >
                                    Revoke (Kaldır)
                                </button>
                            </div>
                            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs text-gray-600 dark:text-gray-400">
                                Lütfen işlem yapmak istediğiniz yetki gruplarını üstteki tablodan (checkbox ile) seçin.
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {employeeGroups.map(m => (
                                        <label key={m.membershipId} className="flex items-center mr-3 mb-1 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="mr-1.5 h-3 w-3 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500" 
                                                checked={selectedMembershipIds.has(m.membershipId)}
                                                onChange={() => toggleMembershipSelect(m.membershipId)}
                                            />
                                            {m.groupName}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Kart Dönüşüm Alanı */}
                        {(isConverting || cardFormats) && (
                            <div className="md:col-span-2 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Raw Kart Formatları</h4>
                                {isConverting ? (
                                    <span className="text-sm text-gray-500">Çevriliyor...</span>
                                ) : cardFormats ? (
                                    <div className="flex flex-wrap gap-4 text-sm">
                                        <div><span className="text-gray-500">DEC:</span> <span className="font-mono ml-1 dark:text-gray-200">{cardFormats.decimalValue}</span></div>
                                        <div><span className="text-gray-500">HEX (26-bit):</span> <span className="font-mono ml-1 dark:text-gray-200">{cardFormats.hex26Bit}</span></div>
                                        <div><span className="text-gray-500">HEX (34-bit):</span> <span className="font-mono ml-1 dark:text-gray-200">{cardFormats.hex34Bit}</span></div>
                                    </div>
                                ) : null}
                            </div>
                        )}

                    </div>
                )}
            </div>

            {/* 409 Çatışma Modalları */}
            <InfoModal
                isOpen={!!cardConflict}
                onClose={() => setCardConflict(null)}
                title="İşlem Onayı"
                type="custom"
            >
                <div className="space-y-4">
                    {cardConflict?.type === 'CARD_IN_USE_BY_ANOTHER' && cardConflict.ownerInfo ? (
                        <div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">Bu kart şu anda aşağıdaki personele aittir:</p>
                            <div className="my-2 p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                                <strong className="font-semibold text-red-600 dark:text-red-400">{`${cardConflict.ownerInfo.firstName} ${cardConflict.ownerInfo.lastName}`}</strong>
                                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">TC: {cardConflict.ownerInfo.turkishIdNo}</span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Kartı bu kişiden devralıp <strong className="font-semibold text-green-600 dark:text-green-400">{`${employee.firstName} ${employee.lastName}`}</strong> isimli personele atamak istediğinizden emin misiniz?
                            </p>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            Bu personelin zaten aktif bir kartı var. Mevcut kartı pasifleştirip bu yeni kartı atamak ister misiniz?
                        </p>
                    )}
                    <div className="flex justify-end gap-2 pt-4">
                        <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-500 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => setCardConflict(null)}>Vazgeç</button>
                        {cardConflict?.type === 'EMPLOYEE_HAS_ACTIVE_CARD' ? (
                            <button
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors"
                                onClick={async () => {
                                    try { await retrySaveWith({ replace: true }); setCardConflict(null); setMsg('Eski kart pasifleştirildi, yeni kart yazıldı.'); }
                                    catch (e) { setErr(e?.message || 'İşlem başarısız.'); }
                                }}
                            >Değiştir</button>
                        ) : (
                            <button
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors"
                                onClick={async () => {
                                    try { await retrySaveWith({ takeover: true }); setCardConflict(null); setMsg('Kart devralındı ve yazıldı.'); }
                                    catch (e) { setErr(e?.message || 'İşlem başarısız.'); }
                                }}
                            >Devral</button>
                        )}
                    </div>
                </div>
            </InfoModal>

            <InfoModal
                isOpen={!!saveConflict}
                onClose={() => setSaveConflict(null)}
                title="İşlem Onayı"
                type="custom"
            >
                <div className="space-y-4">
                    {saveConflict?.type === 'CARD_IN_USE_BY_ANOTHER' && saveConflict.ownerInfo ? (
                        <div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">Bu kart şu anda aşağıdaki personele aittir:</p>
                            <div className="my-2 p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                                <strong className="font-semibold text-red-600 dark:text-red-400">{`${saveConflict.ownerInfo.firstName} ${saveConflict.ownerInfo.lastName}`}</strong>
                                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">TC: {saveConflict.ownerInfo.turkishIdNo}</span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Kartı bu kişiden devralıp <strong className="font-semibold text-green-600 dark:text-green-400">{`${employee.firstName} ${employee.lastName}`}</strong> isimli personele atamak istediğinizden emin misiniz?
                            </p>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            Bu personelin zaten aktif bir kartı var. Mevcut aktif kartı veritabanında pasifleştirip bu kartı atayalım mı?
                        </p>
                    )}
                    <div className="flex justify-end gap-2 pt-4">
                        <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-500 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => setSaveConflict(null)}>Vazgeç</button>
                        {saveConflict?.type === 'EMPLOYEE_HAS_ACTIVE_CARD' ? (
                            <button
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors"
                                onClick={async () => {
                                    try { await retrySaveWith({ replace: true }); setSaveConflict(null); setMsg('Eski kart pasifleştirildi, yeni kart DB’ye kaydedildi.'); }
                                    catch (e) { setErr(e?.message || 'İşlem başarısız.'); }
                                }}
                            >Değiştir</button>
                        ) : (
                            <button
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors"
                                onClick={async () => {
                                    try { await retrySaveWith({ takeover: true }); setSaveConflict(null); setMsg('Kart devralındı ve DB’ye kaydedildi.'); }
                                    catch (e) { setErr(e?.message || 'İşlem başarısız.'); }
                                }}
                            >Devral</button>
                        )}
                    </div>
                </div>
            </InfoModal>
            <InfoModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                title={confirmModal.title}
                message={confirmModal.message}
                type="confirm"
                onConfirm={confirmModal.onConfirm}
            />
        </div>
    );
}